import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, AlertCircle, X } from "lucide-react";

interface FailedCall {
  callId: string;
  queueName: string | null;
  failureReason: string | null;
  stage: string | null;
  retryCount: number;
  createdAt: string;
}

function FailureReasonModal({ reason, onClose }: { reason: string; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: "rgba(0,0,0,0.45)" }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900">Failure Reason</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-5 py-4">
          <pre className="whitespace-pre-wrap break-words text-xs text-red-700 bg-red-50 rounded-md p-4 border border-red-100 font-mono leading-relaxed">
            {reason}
          </pre>
        </div>
        <div className="flex justify-end px-5 py-3 border-t border-gray-200">
          <button
            onClick={onClose}
            className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function FailedTranscripts() {
  const navigate = useNavigate();
  const [calls, setCalls]                   = useState<FailedCall[]>([]);
  const [, setTally]                        = useState<Record<string, number>>({});
  const [total, setTotal]                   = useState(0);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState<string | null>(null);
  const [selectedReason, setSelectedReason] = useState<string | null>(null);

  useEffect(() => {
    fetch("/api/v1/transcripts/failed", {
      headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
    })
      .then((r) => r.json())
      .then((d) => {
        if (!d.success) throw new Error(d.error || "Failed to load");
        setCalls(d.data ?? []);
        setTally(d.tally ?? {});
        setTotal(d.total ?? 0);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6">
      {selectedReason && (
        <FailureReasonModal reason={selectedReason} onClose={() => setSelectedReason(null)} />
      )}
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/transcripts")}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Transcripts
        </button>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Failed Transcriptions</h1>
          <p className="mt-1 text-sm text-gray-500">
            Calls that failed to be transcribed or summarised
          </p>
        </div>
        <span className="rounded-full bg-red-100 px-3 py-1 text-sm font-semibold text-red-700">
          {total} failed
        </span>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          {/* Table */}
          {calls.length === 0 ? (
            <div className="rounded-lg border border-gray-200 bg-white py-16 text-center text-sm text-gray-500">
              <AlertCircle className="mx-auto mb-2 h-8 w-8 text-gray-300" />
              No failed calls found
            </div>
          ) : (
            <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Queue</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Call ID</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Failure Reason</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Stage</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-600">Retries</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {calls.map((call, i) => (
                    <tr key={`${call.callId}-${i}`} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-700">
                        {call.queueName ?? (
                          <span className="text-gray-400 italic">Unknown</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-gray-700">{call.callId}</td>
                      <td className="px-4 py-3">
                        {call.failureReason ? (
                          <button
                            onClick={() => setSelectedReason(call.failureReason!)}
                            className="inline-flex items-center gap-1 rounded bg-red-50 border border-red-100 px-2 py-0.5 text-xs text-red-700 hover:bg-red-100 transition-colors"
                          >
                            <AlertCircle className="h-3 w-3" />
                            View Reason
                          </button>
                        ) : (
                          <span className="text-gray-400 italic text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{call.stage ?? "—"}</td>
                      <td className="px-4 py-3 text-center text-gray-500">{call.retryCount}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                        {call.createdAt
                          ? new Date(call.createdAt).toLocaleDateString("en-US", {
                              month: "short", day: "numeric", year: "numeric",
                            })
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
